import { useMemo } from 'react'
import { Alert, Box, Card, CardContent, CircularProgress, Grid, Typography } from '@mui/material'
import { usePropertyClimateData } from '../../../../hooks/climateData'
import { CanonicalPeriod, EmissionTier, MetricState } from '../../common'
import ClimateMetricCard from '../../common/ClimateMetricCard'
import ClimateSectionTitle from '../../common/ClimateSectionTitle'
import { FreezeThawScoreBadge } from '../../common/freezeThaw/utils'
import MetricStateIndicator, {
  getMostRelevantMetricState,
  MetricValueFallback,
} from '../../common/MetricStateIndicator'
import { getSelectedPeriodPointY, getSucceededMetricData, hasAnyCanonicalPeriodData } from '../overviewMetrics'

const formatDays = (value: number) =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })

type FreezeThawOverviewProps = {
  propertyId: number
  emissionTier: EmissionTier
  selectedPeriod: CanonicalPeriod
}

export default function FreezeThawOverview({ propertyId, emissionTier, selectedPeriod }: FreezeThawOverviewProps) {
  const {
    data: score,
    isLoading: scoreLoading,
    error: scoreError,
  } = usePropertyClimateData('freeze_thaw:score', { propertyId })
  const {
    data: days,
    isLoading: daysLoading,
    error: daysError,
  } = usePropertyClimateData('freeze_thaw:freeze_thaw_days', { propertyId })

  const scorePayload = getSucceededMetricData(score)
  const daysPayload = getSucceededMetricData(days)
  const selectedScore = getSelectedPeriodPointY(scorePayload, emissionTier, selectedPeriod)
  const selectedDays = getSelectedPeriodPointY(daysPayload, emissionTier, selectedPeriod)
  const hasData = useMemo(
    () =>
      hasAnyCanonicalPeriodData([
        period => getSelectedPeriodPointY(scorePayload, emissionTier, period),
        period => getSelectedPeriodPointY(daysPayload, emissionTier, period),
      ]),
    [daysPayload, emissionTier, scorePayload],
  )

  const isLoading = ((scoreLoading && !score) || (daysLoading && !days)) && !hasData
  const hasError = !hasData && (scoreError != null || daysError != null)
  const state = getMostRelevantMetricState([score?.state?.value, days?.state?.value])

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Box mb={2}>
          <ClimateSectionTitle
            title="Overview"
            subtitle="Freeze-thaw conditions for the selected period"
            helpText="A freeze-thaw day has a minimum temperature at or below −2.2°C and a maximum temperature at or above 0°C. Overview metrics use the horizon selected in the page header."
            loading={scoreLoading || daysLoading}
          />
        </Box>

        {isLoading && (
          <Box display="flex" alignItems="center" justifyContent="center" minHeight={140}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!isLoading && hasData && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <ClimateMetricCard
                title="Freeze-Thaw Risk Rating"
                variant="outlined"
                helpText="Risk rating based on the projected annual number of freeze-thaw days for the selected horizon and emissions scenario."
                loading={scoreLoading}
              >
                {selectedScore != null ? (
                  <FreezeThawScoreBadge score={selectedScore} size="compact" />
                ) : (
                  <MetricValueFallback
                    state={score?.state?.value}
                    fallback="Freeze-thaw risk rating is unavailable for this period."
                  />
                )}
              </ClimateMetricCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <ClimateMetricCard
                title="Freeze-Thaw Days"
                variant="outlined"
                helpText="Projected ensemble-mean number of freeze-thaw days per year for the selected horizon."
                loading={daysLoading}
              >
                {selectedDays != null ? (
                  <Typography variant="h5">{formatDays(selectedDays)} days/year</Typography>
                ) : (
                  <MetricValueFallback
                    state={days?.state?.value}
                    fallback="Freeze-thaw days are unavailable for this period."
                  />
                )}
              </ClimateMetricCard>
            </Grid>
          </Grid>
        )}

        {!isLoading && !hasData && (
          <>
            {state && state !== MetricState.SUCCEEDED ? (
              <MetricStateIndicator state={state} variant="block" minHeight={140} />
            ) : (
              <Alert severity={hasError ? 'error' : 'info'}>
                {hasError ? 'Unable to load freeze-thaw overview data.' : 'No freeze-thaw overview data available.'}
              </Alert>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
